import React, { FC } from 'react';
import PDF_EXPORT_COVER_IMAGE from '../images/pdf-export-cover-image.svg';
import { BrandLogo, SocialMediaIcon } from '@kontentino/ui';
import { Page } from 'app/modules/insights/types/Page';
import { Dayjs } from 'dayjs';
import PageTypeUtils from 'app/utils/pageType';
import getSocialMediaTypeForBrandLogo from 'app/utils/getSocialMediaTypeForBrandLogo';
import { useInsightsContainer } from 'components/insights/context';
import { formatDate } from '@kontentino/kontentino-utils/date';
import { useTranslation } from 'react-i18next';

type Props = {
  title?: string;
  page?: Page;
  startDate: Dayjs;
  endDate: Dayjs;
  is12hDateFormat?: boolean;
};

const PdfExportCoverPage: FC<Props> = ({
  title,
  page,
  startDate,
  endDate,
  is12hDateFormat,
}) => {
  const { t } = useTranslation();
  const { filterState } = useInsightsContainer();

  const displayTitle = title || t('pdfReports.cover.defaultTitle');

  return (
    <div className="tw-flex tw-min-h-[297mm] tw-items-center tw-justify-center">
      <div className="tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center tw-p-12">
        <img
          className="tw-mb-8 tw-h-[180px] tw-w-[180px] tw-object-contain"
          src={PDF_EXPORT_COVER_IMAGE}
          alt={t('pdfReports.cover.altText')}
        />
        <div className="tw-mb-2 tw-line-clamp-2 tw-max-w-[750px] tw-text-center tw-text-3xl tw-font-semibold">
          {displayTitle}
        </div>
        <span className="tw-mb-2 tw-block tw-text-center tw-text-xl">
          {`${formatDate(startDate, is12hDateFormat)} - ${formatDate(
            endDate,
            is12hDateFormat,
          )}`}
        </span>
        {filterState.comparedStartDate && filterState.comparedEndDate && (
          <span className="tw-mb-8 tw-block tw-text-center tw-text-lg">
            {`${t('pdfReports.cover.comparedTo')} ${formatDate(
              filterState.comparedStartDate,
              is12hDateFormat,
            )} - ${formatDate(filterState.comparedEndDate, is12hDateFormat)}`}
          </span>
        )}
        {!!page && (
          <div className="tw-mt-8 tw-flex tw-items-center tw-justify-center tw-gap-8">
            <BrandLogo size={80} src={page.picture ?? ''} />
            <div>
              <span className="tw-block tw-text-2xl tw-font-semibold tw-leading-[48px]">
                {page.name}
              </span>
              <div className="tw-flex tw-items-center tw-gap-4">
                <SocialMediaIcon
                  size={28}
                  variant="plain"
                  type={getSocialMediaTypeForBrandLogo(page._type)!}
                />
                <span className="tw-text-lg tw-font-regular tw-leading-[32px]">
                  {PageTypeUtils.getLabel(page._type)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PdfExportCoverPage;
